// Friendly labels + badge tones for the notification/acceptance surface.
// Friendly-label-primary, URNs secondary (frontend-conventions §18). Tones map
// to the shared Badge variants (components/ui/badge.tsx) so theming rides the
// white-label tokens.
//
// i18n: this module is not a component, so it holds translation KEYS, never
// language — the rendering component calls `t()`. The keys are FULLY QUALIFIED
// (`pnr:…`) because these tables are read from more than one namespace
// (`pnr-pax-row` in `pnr`, `pnr-utility-actions` in `pnrActions`), and they point
// at the bundle the passenger surface already ships rather than a second copy:
// one enum → one caption, in every language.

import type { BadgeVariant } from '@/components/ui/badge';
import type {
  AcceptanceFilter,
  NotificationAcceptanceState,
  NotificationChannel,
  NotificationDeliveryState,
} from './types';

export const DELIVERY_LABEL_KEY: Record<NotificationDeliveryState, string> = {
  unknown: 'pnr:delivery.state.unknown',
  pending: 'pnr:delivery.state.pending',
  sent: 'pnr:delivery.state.sent',
  delivered: 'pnr:delivery.state.delivered',
  failed: 'pnr:delivery.state.failed',
};

export const DELIVERY_TONE: Record<NotificationDeliveryState, BadgeVariant> = {
  unknown: 'muted',
  pending: 'info',
  sent: 'info',
  delivered: 'success',
  failed: 'error',
};

export const ACCEPTANCE_LABEL_KEY: Record<NotificationAcceptanceState, string> = {
  none: 'pnr:acceptance.state.none',
  awaiting: 'pnr:acceptance.state.awaiting',
  accepted: 'pnr:acceptance.state.accepted',
  declined: 'pnr:acceptance.state.declined',
};

export const ACCEPTANCE_TONE: Record<NotificationAcceptanceState, BadgeVariant> = {
  none: 'muted',
  awaiting: 'warning',
  accepted: 'success',
  declined: 'error',
};

export const CHANNEL_LABEL_KEY: Record<NotificationChannel, string> = {
  SMS: 'pnr:channel.sms',
  WHATSAPP: 'pnr:channel.whatsapp',
  EMAIL: 'pnr:channel.email',
  VOICE: 'pnr:channel.voice',
  PUSH: 'pnr:channel.push',
};

/** Short copy for the roster acceptance-filter chips. */
export const ACCEPTANCE_FILTER_LABEL_KEY: Record<AcceptanceFilter, string> = {
  all: 'pnr:acceptance.filter.all',
  awaiting: 'pnr:acceptance.filter.awaiting',
  accepted: 'pnr:acceptance.filter.accepted',
  declined: 'pnr:acceptance.filter.declined',
};

/** The filter chips, in display order. */
export const ACCEPTANCE_FILTERS: readonly AcceptanceFilter[] = [
  'all',
  'awaiting',
  'accepted',
  'declined',
];

/** Whether a row's acceptance state passes the chosen filter (`all` passes everything). */
export function matchesAcceptanceFilter(
  state: NotificationAcceptanceState,
  filter: AcceptanceFilter,
): boolean {
  return filter === 'all' || state === filter;
}
